import CompanyChat from "@/components/company/company-chat";
import CompanyEmployeesContent from "@/components/company/company-employees-content";
import { keyContactsDialogStore } from "@/components/data-table-companies/key-contacts-dialog-store";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useChatOpenListener } from "@/hooks/useChatActions";
import { useTranslation } from "@/hooks/useTranslation";
import { useModalOverlay } from "@/stores/modal-overlay-store";
import { CompanyDTO } from "@shaarp/common";
import { GripVerticalIcon, UsersIcon } from "lucide-react";
import { useCallback, useRef, useState, useSyncExternalStore } from "react";

export default function DataTableCellCompanyKeyContacts({
  company,
}: {
  company: CompanyDTO;
}) {
  const { t } = useTranslation();

  if (!company.domain) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <span className="text-sm text-muted-foreground cursor-help">
            {t("components.dataTableCompanies.columns.keyContactsNotAvailable")}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {t(
            "components.dataTableCompanies.columns.keyContactsNotAvailableTooltip"
          )}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Button variant="outline" size="sm" onClick={() => keyContactsDialogStore.open(company)}>
      <UsersIcon className="size-4" />
      {t("components.dataTableCompanies.columns.keyContactsDiscover")}
    </Button>
  );
}

/**
 * Renders the key contacts dialog outside the table to avoid
 * remounts caused by TanStack Table cell re-renders.
 * Must be mounted as a sibling of the DataTable, not inside it.
 */
export function KeyContactsDialog() {
  const { t } = useTranslation();
  const company = useSyncExternalStore(
    keyContactsDialogStore.subscribe,
    keyContactsDialogStore.getSnapshot,
  );

  const [chatOpened, setChatOpened] = useState(false);
  const [chatWidth, setChatWidth] = useState(400);
  const isDragging = useRef(false);

  // The backdrop below is drawn by hand — the Radix root is non-modal, so its
  // own overlay renders nothing — hence the registration by hand too.
  useModalOverlay(company !== null);

  useChatOpenListener(() => {
    setChatOpened(true);
  });

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;

    const onMouseMove = (moveEvent: MouseEvent) => {
      if (!isDragging.current) return;
      const newWidth = window.innerWidth - moveEvent.clientX;
      setChatWidth(Math.max(300, Math.min(newWidth, 800)));
    };

    const onMouseUp = () => {
      isDragging.current = false;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, []);

  return (
    <Dialog
      open={company !== null}
      modal={false}
      onOpenChange={(open) => {
        if (!open) {
          keyContactsDialogStore.close();
          setChatOpened(false);
        }
      }}
    >
      {company && (
        <>
          <DialogPortal>
            <div
              className="fixed inset-0 z-50 bg-black/80"
              onClick={() => {
                keyContactsDialogStore.close();
                setChatOpened(false);
              }}
            />
          </DialogPortal>
          <DialogContent
            className="max-w-[80vw] w-full max-h-[90vh] flex flex-col"
            onInteractOutside={(e) => {
              if (chatOpened) e.preventDefault();
            }}
          >
            <DialogHeader>
              <DialogTitle>
                {t("components.dataTableCompanies.columns.keyContacts")}
                {company.name ? ` — ${company.name}` : ""}
              </DialogTitle>
            </DialogHeader>
            <div className="overflow-auto flex-1 pr-2">
              <CompanyEmployeesContent company={company} />
            </div>
          </DialogContent>
          {chatOpened && (
            <DialogPortal>
              <div
                className="fixed inset-y-0 right-0 z-50 flex"
                style={{ width: chatWidth }}
              >
                <div
                  className="flex items-center justify-center w-px bg-border cursor-col-resize hover:bg-gray-500 active:bg-gray-200 transition-colors after:absolute after:inset-y-0 after:w-2 after:-translate-x-1/2"
                  onMouseDown={handleResizeStart}
                >
                  <div className="z-10 flex h-4 w-3 items-center justify-center rounded-sm border bg-border">
                    <GripVerticalIcon className="size-2.5" />
                  </div>
                </div>
                <div className="relative flex-1 border-l bg-background shadow-lg">
                  <CompanyChat
                    company={company}
                    onClose={() => setChatOpened(false)}
                  />
                </div>
              </div>
            </DialogPortal>
          )}
        </>
      )}
    </Dialog>
  );
}
